import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

import { FlowDefinition } from '@tix/entity'

import { errorTracker } from '@/services/errorTracking'

export const useFlowDefinitionStore = defineStore('flowDefinitions', () => {
  const entities = shallowRef<Record<string, FlowDefinition>>({})
  const data = computed(() => Object.values(entities.value))

  async function loadAll(): Promise<{ error: unknown }> {
    const result = await FlowDefinition.loadAll()

    if (result.error) {
      const error = result.error instanceof Error ? result.error : new Error(String(result.error))
      errorTracker.track(error, { operation: 'flow_definition.list' })
      return { error: result.error }
    }

    entities.value = result.entities
    return { error: null }
  }

  async function load(id: string): Promise<{ data: FlowDefinition | null; error: unknown }> {
    const flow = await FlowDefinition.load(id)

    if (!flow) {
      const error = new Error('Unable to load flow definition')
      errorTracker.track(error, { operation: 'flow_definition.get', flowDefinitionId: id })
      return { data: null, error }
    }

    entities.value = { ...entities.value, [flow.id]: flow }
    return { data: flow, error: null }
  }

  async function getById(id: string): Promise<{ data: FlowDefinition | null; error: unknown }> {
    const flow = entities.value[id]
    return flow ? { data: flow, error: null } : load(id)
  }

  return { data, loadAll, load, getById }
})
